import { useCallback, useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { expiryCode, type BrokerConnectionDto } from '@apna/shared'
import { notifyFail } from '@/components/Toast'
import { useChainLadder } from '@/screens/terminal/useChainLadder'
import type { BasketDto } from '@/types/basket'
import type { BasketSeed } from './basketSeed'
import { BasketChain } from './components/BasketChain'
import { BasketPanel } from './components/BasketPanel'
import { BasketStart } from './components/BasketStart'
import { useBaskets } from './hooks/useBaskets'
import { useBasketDraft } from './hooks/useBasketDraft'
import { useBasketPreview } from './hooks/useBasketPreview'

/**
 * Build a basket, then place it as one labelled group — ROADMAP 9.66.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ITS OWN SURFACE, AND THAT IS A SAFETY DECISION
 *
 * On the Terminal, B and S on the ladder PLACE AN ORDER on the click, with no
 * confirmation (4.15). Here they add a leg. Those are two meanings for one
 * button, and a mode flag on the Terminal would have made which one applies
 * depend on whether a panel happened to be open — on the one control in this
 * product where being wrong costs a live position.
 *
 * A separate surface removes the question instead of answering it with a
 * tint: there is no account selector on this screen, nothing on the ladder
 * can reach the order path, and the only thing that sends is the one button
 * at the bottom of the basket.
 *
 * MOUNTED ONLY WHILE OPEN, by the Terminal. This owns a second ladder — a
 * catalogue, a greeks pass and its own subscriptions — and a component that
 * returns null still runs every hook inside it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS COMPOSED HERE
 *
 * The ladder (`useChainLadder`, shared with the Terminal), the saved drafts
 * (`useBaskets`), the working copy and the send (`useBasketDraft`), and the
 * figures (`useBasketPreview`). This file wires them to three components and
 * holds nothing of its own but which columns the ladder draws.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface BasketBuilderProps {
  onClose: () => void
  /** Where the orders go. Null means no account is chosen yet. */
  connection: BrokerConnectionDto | null
  /** The Terminal refreshes its drawer when a basket lands. */
  onPlaced: () => void
  /** Underlyings recently opened on the Terminal, for the picker. */
  recent?: readonly string[]
  /**
   * Legs handed over by a strategy's ticket — ROADMAP 9.69.
   *
   * Present means the builder skips the "which underlying" step entirely and
   * opens straight onto the ladder with those contracts already in the
   * basket. The trader pressed Adjustments ON something; asking them what
   * they wanted to adjust would be asking a question they just answered.
   */
  seed?: BasketSeed | undefined
}

export default function BasketBuilder({
  onClose,
  connection,
  onPlaced,
  recent = [],
  seed,
}: BasketBuilderProps) {
  const baskets = useBaskets(true)

  const draft = useBasketDraft({ baskets, connection, onPlaced, onDone: onClose })

  /*
   * Empty until a basket is started, and `useChainCatalogue` fetches nothing
   * for an empty underlying — the ladder is simply not drawn yet.
   */
  const ladder = useChainLadder(draft.basket?.underlying ?? '')
  const { expiry, lotSize, greeks, spot } = ladder

  const preview = useBasketPreview({
    legs: draft.legs,
    lotSize: lotSize ?? 0,
    greeks,
    greeksExpiry: expiry,
    spot,
    enabled: draft.legs.length > 0,
  })

  /*
   * ── OPENING ON A STRATEGY'S LEGS — ROADMAP 9.69 ─────────────────────────
   *
   * Created ONCE, guarded by a ref rather than by the dependency list. The
   * create is a round trip that writes a row, and an effect that re-ran —
   * on a re-render, on a new object identity for `seed` — would leave the
   * trader with a drawer full of empty duplicate baskets.
   *
   * It is also why this is an effect at all rather than something the
   * Terminal does before mounting: the server assigns the sequence and
   * therefore the id, and the builder is the only thing that needs them.
   */
  const seeded = useRef(false)
  useEffect(() => {
    if (!seed || seeded.current) return
    seeded.current = true
    draft.openSeed(seed)
  }, [seed, draft])

  const discard = useCallback(
    (row: BasketDto) => {
      void baskets.remove(row.id).catch((e: unknown) => {
        notifyFail(
          'Not deleted',
          e instanceof Error ? e.message : 'The basket could not be deleted.',
        )
      })
    },
    [baskets],
  )

  // Escape closes, as it does for every other overlay on this screen.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const basket = draft.basket

  return (
    <div className="bb-scrim" role="dialog" aria-modal="true" aria-label="Build basket">
      <div className="bb-shell" data-testid="basket-builder">
        <header className="bb-head">
          <span className="bb-head-t">Build basket</span>
          {basket && (
            <span className="bb-head-sub num">
              {basket.underlying}
              <span aria-hidden>·</span>
              {spot > 0 ? spot.toFixed(2) : '—'}
              {expiry && (
                <>
                  <span aria-hidden>·</span>
                  {expiryCode(expiry) ?? expiry}
                </>
              )}
            </span>
          )}
          <span className="flex-1" />
          {/*
            NO VIEW PICKER. The ladder here is read to CHOOSE a strike, which
            is what Market draws: open interest, the price, delta and the
            chance of finishing in the money. Greeks and Combined answer
            questions about a position already on, and this screen has none
            — a control with three settings where one is always right is a
            control that only costs a decision.
          */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="basket-close"
            className="bb-head-x"
          >
            <X size={16} />
          </button>
        </header>

        {basket === null ? (
          <BasketStart
            drafts={baskets.items}
            loading={baskets.loading}
            error={draft.error ?? baskets.error}
            busy={draft.starting}
            onStart={draft.start}
            onOpen={draft.load}
            onDelete={discard}
            recent={recent}
          />
        ) : (
          <div className="bb-split">
            <BasketChain ladder={ladder} underlying={basket.underlying} onAdd={draft.add} />
            <BasketPanel
              name={draft.name}
              onName={draft.setName}
              legs={draft.legs}
              onLots={draft.resize}
              onRemove={draft.drop}
              preview={preview}
              spot={spot}
              /*
               * Only when the seeding strategy actually HAS checks. An empty
               * list is a deliberate empty list — how a trader turns the gate
               * off for one strategy — and drawing an empty panel for it
               * would be a question with no questions in it.
               */
              checklist={
                basket.strategyId && (basket.checklist?.length ?? 0) > 0
                  ? { id: basket.strategyId, checklist: basket.checklist ?? [] }
                  : undefined
              }
              onChecklist={draft.setChecklist}
              checklistReady={draft.checklistReady}
              accountLabel={connection?.label ?? null}
              placing={draft.placing}
              saving={draft.saving}
              saved={draft.saved}
              error={draft.error}
              onPlace={draft.place}
              onSave={draft.save}
            />
          </div>
        )}
      </div>
    </div>
  )
}
